/**
 * plugins/deploy-box.ts — Bloco de comandos de deploy (div.deploy-box)
 *
 * Ferramentas: toggle linha migrate, toggle seed (com input de classe),
 *              toggle queue:restart, adicionar linha custom, remover linha,
 *              mover linha selecionada ↑↓.
 */

import { VtbPlugin, IVtbRegistry, SmartCtx, closestInside } from '../types';

export class DeployBoxPlugin implements VtbPlugin {
    readonly panelId = 'vtb-deploy-box';
    readonly selectorHints = ['div.deploy-box', '.deploy-box', '[data-vtb-id="deploy-box"]'];

    detect(target: HTMLElement, visualEditor: HTMLElement): SmartCtx | null {
        const el = closestInside(target, visualEditor, e => 
            e.classList.contains('deploy-box') || e.dataset.vtbId === 'deploy-box'
        );
        if (!el) return null;
        return { type: this.panelId, label: 'Deploy Box', el };
    }

    activate(ctx: SmartCtx, registry: IVtbRegistry): void {
        const content = ctx.el.innerHTML;

        const btnMigrate = registry.q<HTMLElement>('vtb-deploy-migrate');
        const btnQueue   = registry.q<HTMLElement>('vtb-deploy-queue');
        const seedInput  = registry.q<HTMLInputElement>('vtb-deploy-seed-class');
        const seedForm   = registry.q<HTMLElement>('vtb-deploy-seed-form');

        btnMigrate?.classList.toggle('vtb-btn-active', content.includes('artisan migrate'));
        btnQueue?.classList.toggle('vtb-btn-active', content.includes('queue:restart'));

        const seedMatch = content.match(/artisan db:seed.*?--class[=\s]+([^\s<&]+)/);
        if (seedInput) seedInput.value = seedMatch ? seedMatch[1] : '';
        if (seedForm) seedForm.style.display = content.includes('db:seed') ? 'flex' : 'none';
    }

    setup(registry: IVtbRegistry): void {
        registry.q('vtb-deploy-migrate')?.addEventListener('click', () => this._toggleLine(registry, 'migrate'));
        registry.q('vtb-deploy-queue')?.addEventListener('click',   () => this._toggleLine(registry, 'queue'));
        registry.q('vtb-deploy-seed-toggle')?.addEventListener('click', () => {
            const t = registry.q<HTMLElement>('vtb-deploy-seed-form');
            if (t) t.style.display = t.style.display === 'none' ? 'flex' : 'none';
        });
        registry.q('vtb-deploy-seed-apply')?.addEventListener('click', () => this._applySeed(registry));
        registry.q('vtb-deploy-custom-add')?.addEventListener('click', () => this._addCustom(registry));
        registry.q('vtb-deploy-line-up')?.addEventListener('click',   () => this._moveLine(registry, -1));
        registry.q('vtb-deploy-line-down')?.addEventListener('click', () => this._moveLine(registry,  1));
        registry.q('vtb-deploy-line-del')?.addEventListener('click',  () => this._deleteLine(registry));
    }

    // ── Helpers DOM ──────────────────────────────────────────────────────────

    /** Retorna as linhas de texto do deploy-box como array de nós (Text ou BR) */
    private _lines(box: HTMLElement): { node: Node; text: string }[] {
        // Divide o conteúdo em "linhas" usando <br> como separador
        const result: { node: Node; text: string }[] = [];
        let current = '';
        box.childNodes.forEach(node => {
            if (node.nodeName === 'BR') {
                result.push({ node, text: current.trim() });
                current = '';
            } else {
                current += node.textContent ?? '';
            }
        });
        if (current.trim()) result.push({ node: box.lastChild!, text: current.trim() });
        return result;
    }

    private _rebuildFromLines(box: HTMLElement, lines: string[]): void {
        box.innerHTML = lines.map((l, i) => l + (i < lines.length - 1 ? '<br>' : '')).join('');
    }

    private _getRawLines(box: HTMLElement): string[] {
        return box.innerHTML
            .split('<br>')
            .map(s => s.replace(/<[^>]+>/g, '').trim())
            .filter(Boolean);
    }

    private _setRawLines(box: HTMLElement, lines: string[]): void {
        // Mantém formatação cm/cm-comment para linhas de comentário
        const formatted = lines.map(l => {
            if (l.startsWith('#')) return `<span class="cm">${l}</span>`;
            return l;
        });
        box.innerHTML = formatted.join('<br>\n');
    }

    // ── Operações ─────────────────────────────────────────────────────────────

    private _toggleLine(registry: IVtbRegistry, type: 'migrate' | 'queue'): void {
        const ctx = registry.activeCtx;
        if (!ctx) return;
        const box = ctx.el;
        const lines = this._getRawLines(box);

        const PATTERNS: Record<string, string> = {
            migrate: 'php artisan migrate --step',
            queue:   'php artisan queue:restart',
        };
        const pattern = PATTERNS[type];
        const idx = lines.findIndex(l => l.includes(pattern.split(' ')[2]));
        if (idx >= 0) {
            lines.splice(idx, 1);
            registry.q('vtb-deploy-' + type)?.classList.remove('vtb-btn-active');
        } else {
            lines.push(pattern);
            registry.q('vtb-deploy-' + type)?.classList.add('vtb-btn-active');
        }
        this._setRawLines(box, lines);
        registry.sync();
    }

    private _applySeed(registry: IVtbRegistry): void {
        const ctx = registry.activeCtx;
        if (!ctx) return;
        const box        = ctx.el;
        const className  = registry.q<HTMLInputElement>('vtb-deploy-seed-class')?.value?.trim();
        if (!className) return;

        const lines = this._getRawLines(box);
        const idx   = lines.findIndex(l => l.includes('db:seed'));
        const cmd   = `php artisan db:seed --class=${className}`;
        if (idx >= 0) lines[idx] = cmd;
        else lines.push(cmd);
        this._setRawLines(box, lines);
        const f = registry.q<HTMLElement>('vtb-deploy-seed-form');
        if (f) f.style.display = 'none';
        registry.sync();
    }

    private _addCustom(registry: IVtbRegistry): void {
        const ctx = registry.activeCtx;
        if (!ctx) return;
        const box  = ctx.el;
        const text = registry.q<HTMLInputElement>('vtb-deploy-custom-text')?.value?.trim();
        if (!text) return;
        const lines = this._getRawLines(box);
        lines.push(text);
        this._setRawLines(box, lines);
        const inp = registry.q<HTMLInputElement>('vtb-deploy-custom-text');
        if (inp) inp.value = '';
        registry.sync();
    }

    private _selectedLineIdx(ctx: SmartCtx): number {
        return (ctx.extra?.selectedLine as number | undefined) ?? -1;
    }

    private _moveLine(registry: IVtbRegistry, dir: -1 | 1): void {
        const ctx = registry.activeCtx;
        if (!ctx) return;
        const idx   = this._selectedLineIdx(ctx);
        if (idx < 0) return;
        const lines = this._getRawLines(ctx.el);
        const target = idx + dir;
        if (target < 0 || target >= lines.length) return;
        [lines[idx], lines[target]] = [lines[target], lines[idx]];
        if (ctx.extra) ctx.extra.selectedLine = target;
        this._setRawLines(ctx.el, lines);
        registry.sync();
    }

    private _deleteLine(registry: IVtbRegistry): void {
        const ctx = registry.activeCtx;
        if (!ctx) return;
        const idx = this._selectedLineIdx(ctx);
        if (idx < 0) return;
        const lines = this._getRawLines(ctx.el);
        lines.splice(idx, 1);
        this._setRawLines(ctx.el, lines);
        registry.sync();
    }
}
